import React, { useState, useRef, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from 'next-themes';
import { useNotifications } from '../context/NotificationContext';
import { ValorizaLogo } from './ValorizaLogo';
import {
  LayoutDashboard, Calendar, ClipboardList, CheckSquare, Sparkles,
  Users, Settings, LogOut, Menu, X, Sun, Moon, Globe,
  Briefcase, ChevronRight, ChevronDown, Bell, Download, CalendarDays, ScrollText, Upload, Clock, BarChart3,
  LogIn, LogOut as LogOutIcon, Trash2, CheckCheck, Volume2, VolumeX, FileText, UserPlus, BookUser, ShieldCheck, ExternalLink, Plug
} from 'lucide-react';
import { reportApi } from '../utils/api';
import { Button } from './ui/button';
import { MyTimeReminderDialog } from './MyTimeReminderDialog';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from './ui/dropdown-menu';

const INTRANET_URL = 'https://myhub.valorizasm.com/';

const LogoIcon = () => (
  <a
    href={INTRANET_URL}
    target="_blank"
    rel="noopener noreferrer"
    title="Ir a la intranet Valoriza"
    data-testid="logo-link-intranet"
    className="inline-block rounded-md hover:opacity-80 transition-opacity"
  >
    <ValorizaLogo height="h-8" />
  </a>
);

export default function Layout() {
  const { user, logout } = useAuth();
  const { t, toggleLang, lang } = useLanguage();
  const { theme, setTheme } = useTheme();
  const { notifications, unreadCount, connected, soundEnabled, toggleSound, markAllRead, markOneRead, clearRead } = useNotifications();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef(null);
  const isManagerOrAdmin = user?.role === 'manager' || user?.role === 'admin';

  // Configuración (admin-only) collapsible group
  const configItems = [
    { to: '/admin', icon: Settings, label: 'Panel Admin' },
    { to: '/users', icon: Users, label: 'Usuarios' },
    { to: '/holidays', icon: CalendarDays, label: 'Festivos' },
    { to: '/vacation-balance-admin', icon: CalendarDays, label: 'Saldos vacaciones' },
    { to: '/vacation-records-admin', icon: ScrollText, label: 'Registros vacaciones' },
    { to: '/personal-days-report', icon: BarChart3, label: 'Reporte Asuntos Propios' },
    { to: '/loga-export', icon: FileText, label: 'Exportación LOGA' },
    { to: '/import', icon: Upload, label: 'Importar datos' },
    { to: '/team-overrides', icon: UserPlus, label: 'Excepciones de equipo' },
    { to: '/user-overrides', icon: ShieldCheck, label: 'Bloqueos manuales' },
    { to: '/audit-log', icon: ScrollText, label: 'Auditoría' },
    { to: '/clock-integration', icon: Plug, label: 'API de fichajes' },
  ];
  const isInConfig = configItems.some(it => location.pathname === it.to || location.pathname.startsWith(it.to + '/'));
  const [configOpen, setConfigOpen] = useState(isInConfig);
  useEffect(() => { if (isInConfig) setConfigOpen(true); }, [isInConfig]);

  // Close notif dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const role = user?.role;
  const showConvenios = role === 'admin' || user?.owns_convenios;

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: t('dashboard'), roles: ['employee', 'manager', 'admin'] },
    { to: '/manager', icon: Briefcase, label: 'Panel Manager', roles: ['manager', 'admin'] },
    { to: '/calendar', icon: Calendar, label: t('calendar'), roles: ['employee', 'manager', 'admin'] },
    { to: '/clock', icon: Clock, label: 'Fichar teletrabajo', roles: ['employee', 'manager', 'admin'] },
    { to: '/clock/team', icon: Clock, label: 'Control fichajes', roles: ['manager', 'admin'] },
    { to: '/my-requests', icon: ClipboardList, label: t('myRequests'), roles: ['employee', 'manager', 'admin'] },
    { to: '/approvals', icon: CheckSquare, label: t('approvals'), roles: ['manager', 'admin'] },
    // Informe IA deshabilitado temporalmente a petición de RRHH (hilo "Flex - Teletrabajo duda").
    // Para reactivar, descomentar: { to: '/ai-report', icon: Sparkles, label: t('aiReport'), roles: ['employee', 'manager', 'admin'] },
    ...(showConvenios ? [{ to: '/convenios', icon: Briefcase, label: 'Convenios', roles: ['employee', 'manager', 'admin'] }] : []),
  ];

  const visibleNav = navItems.filter(item => item.roles.includes(role));

  const handleLogout = () => { logout(); navigate('/login'); };

  const initials = user ? `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase() : 'U';

  const roleColors = {
    admin: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    manager: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    employee: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  };

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="flex items-center justify-between px-4 py-5 border-b border-border">
        <LogoIcon />
        <button className="md:hidden p-1 rounded-md hover:bg-accent" onClick={() => setSidebarOpen(false)}>
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {visibleNav.map((item) => {
          const active = location.pathname === item.to ||
            (item.to !== '/clock' && location.pathname.startsWith(item.to + '/')) ||
            (item.to === '/clock' && location.pathname === '/clock');
          return (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setSidebarOpen(false)}
              data-testid={`nav-${item.to.replace('/', '')}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 group ${
                active
                  ? 'bg-brand text-white shadow-sm'
                  : 'text-foreground/70 hover:bg-accent hover:text-foreground'
              }`}
            >
              <item.icon className={`h-4 w-4 flex-shrink-0 ${active ? 'text-white' : 'text-muted-foreground group-hover:text-foreground'}`} />
              <span>{item.label}</span>
              {active && <ChevronRight className="h-3 w-3 ml-auto text-white/70" />}
            </NavLink>
          );
        })}

        {/* Configuración (admin only) */}
        {role === 'admin' && (
          <div className="pt-1">
            <button
              type="button"
              onClick={() => setConfigOpen(o => !o)}
              data-testid="nav-config-toggle"
              aria-expanded={configOpen}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 group ${
                isInConfig
                  ? 'bg-brand/10 text-brand'
                  : 'text-foreground/70 hover:bg-accent hover:text-foreground'
              }`}
            >
              <Settings className={`h-4 w-4 flex-shrink-0 ${isInConfig ? 'text-brand' : 'text-muted-foreground group-hover:text-foreground'}`} />
              <span>Configuración</span>
              {configOpen
                ? <ChevronDown className="h-3.5 w-3.5 ml-auto opacity-70" />
                : <ChevronRight className="h-3.5 w-3.5 ml-auto opacity-70" />}
            </button>
            {configOpen && (
              <div className="mt-1 ml-3 pl-3 border-l border-border/60 space-y-0.5">
                {configItems.map((item) => {
                  const active = location.pathname === item.to || location.pathname.startsWith(item.to + '/');
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={() => setSidebarOpen(false)}
                      data-testid={`nav-${item.to.replace('/', '')}`}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-150 group ${
                        active
                          ? 'bg-brand text-white shadow-sm'
                          : 'text-foreground/70 hover:bg-accent hover:text-foreground'
                      }`}
                    >
                      <item.icon className={`h-3.5 w-3.5 flex-shrink-0 ${active ? 'text-white' : 'text-muted-foreground group-hover:text-foreground'}`} />
                      <span className="truncate">{item.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </nav>

      {/* Quick actions */}
      <div className="px-3 py-3 border-t border-border space-y-1">
        {(() => {
          const vacDisabled = !user?.fecha_inicio;
          const vacDisabledMsg = 'Tu puesto y calendario no están formalizados (falta fecha de inicio). Contacta con Admón. de Personal.';
          return (
            <button
              data-testid="btn-new-vacation"
              onClick={() => {
                if (vacDisabled) { alert(vacDisabledMsg); return; }
                navigate('/vacations/new'); setSidebarOpen(false);
              }}
              title={vacDisabled ? vacDisabledMsg : undefined}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                vacDisabled
                  ? 'text-muted-foreground/50 cursor-not-allowed'
                  : 'text-muted-foreground hover:bg-green-50 hover:text-green-700 dark:hover:bg-green-900/20 dark:hover:text-green-400'
              }`}
            >
              <span className={`h-2 w-2 rounded-full flex-shrink-0 ${vacDisabled ? 'bg-muted-foreground/40' : 'bg-vacation'}`} />
              {t('newVacation')}
            </button>
          );
        })()}
        <button
          data-testid="btn-new-remote"
          onClick={() => { navigate('/remote/new'); setSidebarOpen(false); }}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-blue-50 hover:text-blue-700 dark:hover:bg-blue-900/20 dark:hover:text-blue-400 transition-colors"
        >
          <span className="h-2 w-2 rounded-full bg-telework flex-shrink-0" />
          {t('newRemote')}
        </button>
        {user?.convenio_id && (
          <button
            data-testid="btn-new-personal-day"
            onClick={() => { navigate('/personal-days/new'); setSidebarOpen(false); }}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-amber-50 hover:text-amber-700 dark:hover:bg-amber-900/20 dark:hover:text-amber-400 transition-colors"
          >
            <span className="h-2 w-2 rounded-full bg-amber-500 flex-shrink-0" />
            Asunto propio
          </button>
        )}
      </div>

      {/* Manual PDF download */}
      <div className="px-3 pb-2 border-t border-border pt-2">
        <a
          data-testid="btn-download-manual"
          href="#"
          onClick={async (e) => {
            e.preventDefault();
            try {
              const res = await reportApi.downloadUserGuide();
              const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
              const a = document.createElement('a');
              a.href = url;
              a.download = 'Manual_Usuario_Valoriza_HR.pdf';
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              window.URL.revokeObjectURL(url);
            } catch (e) {
              console.error('Error descargando manual:', e);
            }
          }}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-green-50 hover:text-green-700 dark:hover:bg-green-900/20 dark:hover:text-green-400 transition-colors cursor-pointer"
        >
          <Download className="h-4 w-4 flex-shrink-0" />
          Manual de Usuario (PDF)
        </a>
      </div>

      {/* User info */}
      <div className="px-3 py-3 border-t border-border">
        <div className="flex items-center gap-3">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-brand text-white text-xs font-bold">{initials}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold truncate">{user?.first_name} {user?.last_name}</p>
            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${roleColors[role]}`}>
              {t(role)}
            </span>
          </div>
          <button onClick={handleLogout} className="p-1.5 rounded-md hover:bg-destructive/10 hover:text-destructive transition-colors" title={t('logout')}>
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Sidebar Desktop */}
      <aside className="hidden md:flex flex-col w-64 border-r border-border bg-card flex-shrink-0">
        <SidebarContent />
      </aside>

      {/* Sidebar Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="relative flex flex-col w-72 h-full bg-card border-r border-border animate-slide-in">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top header */}
        <header className="flex items-center justify-between px-4 md:px-6 py-3 border-b border-border bg-card/80 backdrop-blur-sm flex-shrink-0">
          <div className="flex items-center gap-3">
            <button
              className="md:hidden p-2 rounded-lg hover:bg-accent transition-colors"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="hidden md:block">
              <h1 className="text-base font-semibold font-heading text-foreground">
                {[...visibleNav, ...(role === 'admin' ? configItems : [])].sort((a, b) => b.to.length - a.to.length).find(n => location.pathname === n.to || location.pathname.startsWith(n.to + '/'))?.label || 'Valoriza HR'}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Notification bell (managers/admins) */}
            {isManagerOrAdmin && (
              <div className="relative" ref={notifRef}>
                <button
                  onClick={() => { setNotifOpen(o => !o); if (unreadCount > 0 && !notifOpen) {} }}
                  data-testid="btn-notifications"
                  className="relative p-2 rounded-xl hover:bg-accent transition-colors"
                  title={connected ? 'Notificaciones en tiempo real activas' : 'Reconectando...'}
                >
                  <Bell className={`h-4 w-4 ${unreadCount > 0 ? 'text-brand animate-wiggle' : 'text-muted-foreground'}`} />
                  {unreadCount > 0 && (
                    <span
                      data-testid="notif-badge"
                      className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center animate-bounce-once"
                    >
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                  {!connected && (
                    <span className="absolute bottom-0 right-0 h-2 w-2 rounded-full bg-amber-400 border border-card" title="Reconectando..." />
                  )}
                </button>

                {/* Notification dropdown */}
                {notifOpen && (
                  <div className="absolute right-0 top-full mt-2 w-80 bg-card border border-border rounded-2xl shadow-2xl z-50 overflow-hidden">
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold font-heading">Notificaciones</span>
                        {connected
                          ? <span className="flex items-center gap-1 text-[10px] text-green-600 dark:text-green-400"><span className="h-1.5 w-1.5 bg-green-500 rounded-full animate-pulse" />En vivo</span>
                          : <span className="flex items-center gap-1 text-[10px] text-amber-600"><span className="h-1.5 w-1.5 bg-amber-400 rounded-full" />Reconectando</span>
                        }
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={toggleSound} title={soundEnabled ? 'Silenciar sonido' : 'Activar sonido'}
                          className={`p-1.5 rounded-lg transition-colors ${soundEnabled ? 'text-brand hover:bg-brand/10' : 'text-muted-foreground hover:bg-accent'}`}>
                          {soundEnabled ? <Volume2 className="h-3.5 w-3.5" /> : <VolumeX className="h-3.5 w-3.5" />}
                        </button>
                        {unreadCount > 0 && (
                          <button onClick={markAllRead} title="Marcar todas como leídas"
                            className="p-1.5 rounded-lg text-muted-foreground hover:text-brand hover:bg-brand/10 transition-colors">
                            <CheckCheck className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <button onClick={clearRead} title="Eliminar leídas"
                          className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* List */}
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <div className="py-8 text-center">
                          <Bell className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
                          <p className="text-xs text-muted-foreground">Sin notificaciones</p>
                        </div>
                      ) : notifications.map(n => (
                        <button
                          key={n.id}
                          onClick={() => { markOneRead(n.id); navigate('/clock/team'); setNotifOpen(false); }}
                          data-testid={`notif-item-${n.id}`}
                          className={`w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-muted/40 transition-colors border-b border-border/50 last:border-0 ${!n.read ? 'bg-brand/5' : ''}`}
                        >
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                            n.type === 'clock_in' ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-orange-100 dark:bg-orange-900/30'
                          }`}>
                            {n.type === 'clock_in'
                              ? <LogIn className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                              : <LogOutIcon className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                            }
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs leading-tight ${!n.read ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                              {n.message}
                            </p>
                            <p className="text-[10px] text-muted-foreground mt-0.5">
                              {new Date(n.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                              {' · '}
                              {new Date(n.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
                            </p>
                          </div>
                          {!n.read && <span className="h-2 w-2 bg-brand rounded-full flex-shrink-0 mt-1" />}
                        </button>
                      ))}
                    </div>

                    {/* Footer */}
                    {notifications.length > 0 && (
                      <div className="px-4 py-2 border-t border-border bg-muted/20 text-center">
                        <button onClick={() => { navigate('/clock/team'); setNotifOpen(false); }}
                          className="text-xs text-brand hover:underline font-medium">
                          Ver control de fichajes →
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Intranet shortcut — always visible */}
            <Button
              variant="outline"
              size="sm"
              asChild
              data-testid="btn-intranet"
              className="gap-1.5 hidden sm:inline-flex border-emerald-200 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-900/40 dark:text-emerald-400 dark:hover:bg-emerald-900/20"
            >
              <a href={INTRANET_URL} target="_blank" rel="noopener noreferrer" title="Ir a la intranet Valoriza">
                Intranet <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </Button>
            <a
              href={INTRANET_URL}
              target="_blank"
              rel="noopener noreferrer"
              title="Ir a la intranet Valoriza"
              data-testid="btn-intranet-mobile"
              className="sm:hidden p-2 rounded-lg text-emerald-700 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-900/20"
            >
              <ExternalLink className="h-4 w-4" />
            </a>

            {/* Language toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLang}
              data-testid="lang-toggle"
              className="gap-1 text-xs font-medium"
            >
              <Globe className="h-4 w-4" />
              {lang.toUpperCase()}
            </Button>

            {/* Theme toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
              data-testid="theme-toggle"
              className="h-8 w-8"
            >
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>

            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-accent transition-colors" data-testid="user-menu">
                  <Avatar className="h-7 w-7">
                    <AvatarFallback className="bg-brand text-white text-xs font-bold">{initials}</AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:block text-sm font-medium">{user?.first_name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.first_name} {user?.last_name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/dashboard')}>{t('dashboard')}</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                  <LogOut className="h-4 w-4 mr-2" />{t('logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <div className="p-4 md:p-6 lg:p-8 animate-fade-in">
            <Outlet />
          </div>
        </main>
      </div>

      {/* My Time reminder shown on telework days */}
      <MyTimeReminderDialog />
    </div>
  );
}
